<div class="flex flex-col pr-2 pl-2 md:pl-6">
  @if (canChangePassword) {
    <h2 class="title-large text-primary mb-1">{{ 'Password' | translate }}</h2>
    <p class="body-medium text-on-surface-variant mb-4 md:mb-8">{{ 'Please enter your current password to change to a new password' | translate }}</p>

    <form [formGroup]="passwordForm" class="flex flex-col">
      <!-- Current password -->
      <div class="flex flex-col md:flex-row items-start mb-2">
        <div class="w-full md:w-1/3 mb-1 md:mb-0">
          <div class="w-4/5 label-xlarge text-on-surface-variant">{{ 'Current password' | translate }}</div>
        </div>
        <div class="w-full md:w-2/3">
          <div class="w-full lg:w-4/5">
            <mat-form-field appearance="outline" class="w-full">
              <mat-label>{{ 'Current password' | translate }}</mat-label>
              <input 
                matInput 
                [type]="showCurrentPassword ? 'text' : 'password'" 
                formControlName="currentPassword" 
                [placeholder]="'Enter current password' | translate"
                data-testid="current-password-input"
              />
              <button 
                mat-icon-button 
                matSuffix 
                type="button"
                (click)="togglePasswordVisibility('current')"
                class="text-on-surface-variant"
              >
                <mat-icon [svgIcon]="showCurrentPassword ? 'eye_opened' : 'eye_closed'"></mat-icon>
              </button>
              <mat-error *ngIf="passwordForm.get('currentPassword')?.hasError('required')">
                {{ 'Current password is required' | translate }}
              </mat-error>
              <mat-error *ngIf="passwordForm.get('currentPassword')?.hasError('whitespace')">
                {{ 'Password cannot contain whitespace' | translate }}
              </mat-error>
            </mat-form-field>
          </div>
        </div>
      </div>

      <!-- New password -->
      <div class="flex flex-col md:flex-row items-start mb-2">
        <div class="w-full md:w-1/3 mb-1 md:mb-0">
          <div class="w-4/5 label-xlarge text-on-surface-variant">{{ 'New password' | translate }}</div>
        </div>
        <div class="w-full mb-3 md:w-2/3">
          <div class="w-full lg:w-4/5">
            <app-new-password
              [control]="passwordForm.get('newPassword')"
              [passwordPattern]="resetPassword.getPattern()"
              [label]="'New password' | translate"
              [placeholder]="'Enter new password' | translate">
            </app-new-password>
          </div>
        </div>
      </div>

      <!-- Confirm password -->
      <div class="flex flex-col md:flex-row items-start mb-3">
        <div class="w-full md:w-1/3 mb-1 md:mb-0">
          <div class="w-4/5 label-xlarge text-on-surface-variant">{{ 'Confirm password' | translate }}</div>
        </div>
        <div class="w-full md:w-2/3">
          <div class="w-full lg:w-4/5">
            <mat-form-field appearance="outline" class="w-full">
              <mat-label>{{ 'Confirm new password' | translate }}</mat-label>
              <input 
                matInput 
                [type]="showConfirmPassword ? 'text' : 'password'" 
                formControlName="confirmPassword" 
                [placeholder]="'Re-enter password' | translate"
                data-testid="confirm-password-input"
              />
              <button 
                mat-icon-button 
                matSuffix 
                type="button"
                (click)="togglePasswordVisibility('confirm')"
                class="text-on-surface-variant"
              >
                <mat-icon [svgIcon]="showConfirmPassword ? 'eye_opened' : 'eye_closed'"></mat-icon>
              </button>
              <mat-error *ngIf="passwordForm.get('confirmPassword')?.hasError('required')">{{ 'Please confirm your password' | translate }}
              </mat-error>
              <mat-error *ngIf="passwordForm.get('confirmPassword')?.hasError('whitespace')">{{ 'Password cannot contain whitespace' | translate }}
              </mat-error>
              <mat-error *ngIf="passwordForm.get('confirmPassword')?.hasError('passwordMismatch')">{{ 'Passwords do not match' | translate }}
              </mat-error>
            </mat-form-field>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex flex-col md:flex-row items-start sticky bottom-0 z-10 pt-4 pb-6 bg-surface-container-lowest">
        <div class="hidden md:block w-1/3"></div>
        <div class="w-full md:w-2/3 flex">
          <div class="w-full lg:w-4/5 flex items-end gap-4 justify-end">
            <button class="flex-1 [--mdc-outlined-button-container-height:36px] 
              md:flex-none md:[--mdc-outlined-button-container-height:48px]"
              mat-stroked-button color="primary" type="button" (click)="cancel()" [disabled]="isLoadingPassword" data-testid="password-cancel-button">
              {{ 'Cancel' | translate }}
            </button>
            <button
              class="flex-1 [--mdc-filled-button-container-height:36px] 
              md:flex-none md:[--mdc-filled-button-container-height:48px]"
              mat-flat-button 
              color="primary" 
              type="button" 
              (click)="changePassword()" 
              [disabled]="!passwordForm.valid || isLoadingPassword"
              data-testid="password-save-button"
            >
              <app-loading-spinner [loading]="isLoadingPassword" [diameter]="20">
                {{ 'Save changes' | translate }}
              </app-loading-spinner>
            </button>
          </div>
        </div>
      </div>

      <div class="border-t border-surface-container mb-6"></div>

    </form>
  }

  <h2 class="title-large text-primary mb-1 mt-2.5">{{ 'Encounter' | translate }}</h2>
  <p class="body-medium text-on-surface-variant mb-8" data-testid="encounter-security-helper">{{ (isConsentCentrallyManaged ? 'Contact your administrator to manage encounter security preferences' : 'Manage encounter security preferences') | translate }}</p>

  <form [formGroup]="encounterForm" class="flex flex-col">
    <!-- Enable patient consent -->
    <div class="flex flex-col md:flex-row items-start mb-4" [class.opacity-50]="isConsentCentrallyManaged" [class.pointer-events-none]="isConsentCentrallyManaged">
      <div class="w-full md:w-1/3 mb-2 md:mb-0">
        <div class="w-4/5 label-xlarge text-on-surface-variant">{{ 'Patient consent' | translate }}</div>
      </div>
      <div class="w-full md:w-2/3">
        <div class="w-full lg:w-4/5">
          <div class="bg-on-primary border border-surface-varient rounded p-4 flex flex-col md:flex-row items-start gap-4">
            <div class="p-2">
              <mat-icon class="material-symbols-outlined scale-125">check_circle</mat-icon>
            </div>
            <div class="w-full">
              <div class="title-medium text-on-surface">{{ 'Enable patient consent' | translate }}</div>
              <div class="body-medium text-on-surface-variant">{{ 'Require confirmation of patient consent before starting a consultation' | translate }}</div>
            </div>
            <mat-slide-toggle color="primary" formControlName="enablePatientConsent" class="mt-3 md:mt-1 md:ml-3" data-testid="patient-consent-toggle"></mat-slide-toggle>
          </div>
        </div>
      </div>
    </div>

    <!-- Consent message -->
    <div class="flex flex-col md:flex-row items-start mb-4" [class.opacity-50]="isConsentCentrallyManaged" [class.pointer-events-none]="isConsentCentrallyManaged">
      <div class="w-full md:w-1/3 mb-2 md:mb-0">
        <div class="w-4/5 label-xlarge text-on-surface-variant">{{ 'Consent message' | translate }}</div>
      </div>
      <div class="w-full md:w-2/3">
        <div class="w-full lg:w-4/5">
          <div class="bg-on-primary border border-surface-varient rounded-lg p-4">
            <mat-form-field appearance="outline" class="w-full">
              <mat-label>{{ 'Consent message' | translate }}</mat-label>
              <textarea matInput formControlName="consentMessage" maxlength="150" rows="3" [placeholder]="'Enter consent message' | translate" [readonly]="isConsentCentrallyManaged || !encounterForm.get('enablePatientConsent')?.value" data-testid="consent-message-input"></textarea>
              <mat-hint align="end">{{ encounterForm.get('consentMessage')?.value?.length || 0 }}/150</mat-hint>
              <mat-error *ngIf="encounterForm.get('consentMessage')?.hasError('required')">
                {{ 'Consent message is required' | translate }}
              </mat-error>
            </mat-form-field>
          </div>
        </div>
      </div>
    </div>

    <!-- Enable feedback sounds -->
    <div class="flex flex-col md:flex-row items-start mb-6">
      <div class="w-full md:w-1/3 mb-2 md:mb-0">
        <div class="w-4/5 label-xlarge text-on-surface-variant">{{ 'Enable beep sound' | translate }}</div>
      </div>
      <div class="w-full md:w-2/3">
        <div class="w-full lg:w-4/5">
          <div class="bg-on-primary border border-surface-varient rounded-lg p-4 flex flex-col md:flex-row items-start min-h-16 gap-4">
            <div class="p-2">
              <mat-icon fontSet="material-symbols-outlined" class="scale-125">notifications_active</mat-icon>
            </div>
            <div class="w-full">
              <div class="title-medium text-on-surface">{{ 'Enable feedback sounds' | translate }}</div>
              <div class="body-medium text-on-surface-variant">{{ 'Enable feedback sounds when recording' | translate }}</div>
            </div>
            <mat-slide-toggle color="primary" formControlName="recorderFeedbackSound" class="mt-3 md:mt-1 md:ml-3" data-testid="feedback-sound-toggle"></mat-slide-toggle>
          </div>
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex flex-col md:flex-row items-start sticky bottom-0 z-10 pt-4 pb-6 bg-surface-container-lowest">
      <div class="hidden md:block w-1/3"></div>
      <div class="w-full md:w-2/3 flex">
        <div class="w-full lg:w-4/5 flex items-end gap-4 justify-end">
          <button class="flex-1 [--mdc-outlined-button-container-height:36px] md:flex-none md:[--mdc-outlined-button-container-height:48px]"
            mat-stroked-button color="primary" type="button" (click)="cancelEncounter()" [disabled]="isLoadingEncounter" data-testid="encounter-cancel-button">
            {{ 'Cancel' | translate }}</button>
          <button class="flex-1 [--mdc-filled-button-container-height:36px] md:flex-none md:[--mdc-filled-button-container-height:48px]"
            mat-flat-button color="primary" type="button" (click)="saveEncounterSettings()" [disabled]="encounterForm.pristine || isLoadingEncounter" data-testid="encounter-save-button">
            <app-loading-spinner [loading]="isLoadingEncounter" [diameter]="20">
              {{ 'Save changes' | translate }}
            </app-loading-spinner>
          </button>
        </div>
      </div>
    </div>
  </form>
</div>
